import React from 'react';
import Confetti from 'react-confetti';

/**
 * PathCompletionModal Component
 * Displays celebration screen when user completes entire learning path
 *
 * @param {Object} props
 * @param {Object} props.studentPath - The completed learning path
 * @param {number} props.pathCoursesLength - Total number of courses in path
 * @param {boolean} props.showConfetti - Whether to show confetti animation
 * @param {Object} props.windowDimension - Window dimensions for confetti
 * @param {Function} props.onClose - Handler to close the modal
 */
const PathCompletionModal = ({
  studentPath,
  pathCoursesLength,
  showConfetti,
  windowDimension,
  onClose
}) => {
  return (
    <div className="text-center py-6 sm:py-12 relative">
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <Confetti
            width={windowDimension.width}
            height={windowDimension.height}
            gravity={0.3}
            numberOfPieces={200}
            recycle={false}
          />
        </div>
      )}
      <div className="mb-6 sm:mb-8">
        {/* Celebration Icon */}
        <div className="relative mb-4 sm:mb-6">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 sm:w-32 sm:h-32 bg-[#1086a4] rounded-full opacity-20 animate-pulse"></div>
          </div>
          <div className="relative flex items-center justify-center">
            <span className="text-6xl sm:text-8xl animate-bounce">🎉</span>
          </div>
        </div>

        {/* Congratulations Text */}
        <h2 className="text-3xl sm:text-4xl font-bold text-[#054581] mb-3 sm:mb-4">
          Congratulations!
        </h2>

        <p className="text-lg sm:text-xl text-[#1e2d40] mb-2">
          You've successfully completed all courses in
        </p>

        <h3 className="text-xl sm:text-2xl font-bold text-[#054581] mb-4 sm:mb-6">
          {studentPath?.title}
        </h3>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 max-w-md mx-auto">
          <div className="text-center">
            <p className="text-base sm:text-lg text-[#1e2d40] mb-3">
              🏆 Path Statistics 🏆
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[#1086a4] font-semibold">Courses Completed</div>
                <div className="text-xl sm:text-2xl font-bold text-[#054581]">{pathCoursesLength}</div>
              </div>
              <div>
                <div className="text-[#1086a4] font-semibold">Progress</div>
                <div className="text-xl sm:text-2xl font-bold text-green-600">100%</div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-[#1e2d40] mb-4 sm:mb-6 max-w-lg mx-auto leading-relaxed text-sm sm:text-base">
          You've mastered every lesson and conquered every challenge. Your dedication and hard work have paid off!
          You're now ready to apply your new skills and knowledge.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pb-20 lg:pb-0">
          <button
            onClick={onClose}
            className="bg-[#1086a4] hover:bg-[#054581] text-white px-6 sm:px-8 py-3 rounded-lg font-semibold transition-colors shadow-sm w-full sm:w-auto"
          >
            Back to Dashboard
          </button>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(`I just completed the ${studentPath?.title} learning path! 🎉`);
            }}
            className="bg-gray-50 hover:bg-gray-100 text-[#1086a4] px-4 sm:px-6 py-3 rounded-lg font-semibold transition-colors border border-gray-200 w-full sm:w-auto"
          >
            Share Achievement
          </button>
        </div>
      </div>
    </div>
  );
};

export default PathCompletionModal;
